// Project Sentinel - Unified connector API types (/api/v1/connectors).
// Mirrors backend/app/schemas/connector.py — one shape for every connector.
import type { SupersedeCandidate } from './index'

export type ItemKind = 'site' | 'meter' | 'asset'
/** `closed`: still listed by the provider but no data for months — shown, never selectable.
 * `not_reported`: linked, but the provider no longer lists it — shown so it can be unlinked. */
export type ItemStatus =
  | 'linked' | 'reconnectable' | 'matched' | 'new' | 'ignored' | 'closed' | 'not_imported' | 'not_reported'
export type FirstDataSource = 'provider' | 'probe' | 'probe_partial' | 'fy_floor'
export type LinkAction = 'created' | 'reconciled' | 'reactivated' | 'unchanged' | 'unlinked'
export type RunStatus = 'running' | 'completed' | 'failed' | 'cancelled'

export interface CredentialField {
  name: string
  label: string
  type: 'text' | 'password' | 'number' | 'select' | 'email'
  required: boolean
  secret: boolean
  identity: boolean
  options_field: string | null
  help: string | null
}

export interface ExtraColumn {
  key: string
  label: string
  kinds: ItemKind[]
  editable: boolean
  /** Editable column: its values come from GET /connectors/{c}/options/{options_field}. */
  options_field?: string | null
}

export interface ConnectorInfo {
  key: string
  label: string
  country: string | null
  credential_fields: CredentialField[]
  login_cardinality: '1:1' | 'N:1'
  kinds: ItemKind[]
  extra_columns: ExtraColumn[]
  /** `consent_link`: the wizard's first step sends the person to the provider's consent page. */
  login_flow?: 'credentials' | 'consent_link'
  /** Days before `credentials_expires_at` a connection counts as expiring (Renew). */
  renew_within_days?: number
}

export interface OptionItem {
  value: string
  label: string
  extra?: Record<string, unknown>
}

export interface ValidateResult {
  valid: boolean
  error: string | null
  details: Record<string, unknown>
}

export interface ConnectionOut {
  id: string
  connector: string
  name: string
  status: string
  customer_id?: string | null
  connection_id?: string | null
  identity_id?: string | null
  item_counts?: Record<string, number>
  pending_inbox?: number
  last_sync_at?: string | null
  last_error?: unknown
  created_at?: string | null
  /** When the stored login stops working (UTC), e.g. an Eloverblik refresh token's expiry. */
  credentials_expires_at?: string | null
  /** Whole days left (Copenhagen calendar; negative = expired); null when unknown. */
  credentials_expire_in_days?: number | null
  /** Expires within 30 days (or has expired): renew via reconnect. */
  credentials_expiring?: boolean
  extra?: Record<string, unknown>
}

export interface ConnectionCreateBody {
  name?: string | null
  credentials?: Record<string, unknown>
  identity_id?: string
}

export type IdentityStatus = 'active' | 'error' | 'reauth_required' | 'archived'

/** A stored login (GET /connectors/{c}/identities). Never contains a secret. */
export interface IdentityOut {
  id: string
  connector: string
  label: string
  status: IdentityStatus
  connection_id?: string | null
  created_at?: string | null
  archived_at?: string | null
  /** Identity (non-secret) credential fields. */
  fields: Record<string, unknown>
}

export interface Page<T> {
  items: T[]
  next_cursor: string | null
  total: number | null
}

export interface MatchOut {
  building_id: string
  building_name: string
  method: string
  score: number
  preselect: boolean
}

export interface LinkedOut {
  building_id: string | null
  building_name: string | null
  meter_id: string | null
  asset_id: string | null
  data_source_id: string | null
}

export interface DiscoveredItem {
  external_key: string
  kind: ItemKind
  name: string
  street?: string | null
  house_no?: string | null
  postcode?: string | null
  city?: string | null
  store_code?: string | null
  country?: string | null
  energy_source: string | null
  provider_energy_type?: string | null
  unit?: string | null
  resolution?: string | null
  /** Meter hierarchy: a meter with a parent never counts toward totals. */
  parent_key?: string | null
  /** The `site` item this item is listed under (e.g. a Shelly room): display only. */
  group_key?: string | null
  /** The `asset` item this meter links to (linked along with the meter). */
  asset_key?: string | null
  /** Offered, never preselected (e.g. a main meter's returned energy). */
  optional?: boolean
  is_main_hint?: boolean | null
  first_data_date?: string | null
  first_data_source?: FirstDataSource | null
  match: MatchOut | null
  status: ItemStatus
  /** Why the item has its status (set for `not_imported`, e.g. "Water is not imported from ista"). */
  status_reason?: string | null
  linked: LinkedOut | null
  extra: Record<string, unknown>
}

export interface DiscoverStarted {
  run_id: string
  status: RunStatus
}

export interface DiscoverSummary {
  total: number
  by_kind: Record<string, number>
  by_status: Record<string, number>
}

export interface DiscoverRunOut {
  run_id: string
  connection_id: string
  status: RunStatus
  started_at: string | null
  completed_at: string | null
  progress: { phase?: string; done?: number; total?: number; [k: string]: unknown }
  summary: DiscoverSummary
  items: DiscoveredItem[]
  next_cursor: string | null
  total: number
  error: string | null
}

export interface LinkItemRequest {
  external_key: string
  kind: ItemKind
  building_id?: string | null
  action?: 'link' | 'unlink'
  energy_source?: string | null
  parent_key?: string | null
  is_main?: boolean | null
  first_data_date?: string | null
  details?: Record<string, unknown>
}

export interface LinkResult {
  external_key: string
  kind: ItemKind
  ok: boolean
  action: LinkAction | null
  building_id: string | null
  meter_id: string | null
  asset_id: string | null
  data_source_id: string | null
  energy_source: string | null
  counts_toward_totals: boolean | null
  supersedes_candidates: SupersedeCandidate[]
  error_code: string | null
  error: string | null
}

export interface LinkResponse {
  dry_run: boolean
  results: LinkResult[]
  linked: number
  failed: number
  supersedes_candidates: SupersedeCandidate[]
}

export interface SyncRequestBody {
  start_date?: string | null
  end_date?: string | null
  external_keys?: string[] | null
}

/** SyncJob.status of a catch-up the backend accepted but runs at its next sync tick (NomOS: the rail's next
 *  tick runs the connection's approved schedule, reading each meter from its last reading). Not active: no
 *  progress to poll. */
export const SYNC_JOB_QUEUED = 'queued'

export interface SyncJob {
  id: string
  connection_id: string
  status: string
  start_date: string | null
  end_date: string | null
  items_total: number | null
  items_processed: number | null
  records_inserted: number | null
  progress_percent: number | null
  current_item: string | null
  current_period: string | null
  errors: unknown[]
  started_at: string | null
  completed_at: string | null
}

export interface ItemSyncStatus {
  external_key: string
  kind: ItemKind
  meter_id: string | null
  status: string
  start_date: string | null
  first_data_source: FirstDataSource | null
  last_reading_at: string | null
  error: string | null
}

export interface SyncStatusOut {
  active_job: SyncJob | null
  items: ItemSyncStatus[]
  next_cursor: string | null
  total: number
}

export interface AggregateWindow {
  cagg: string
  start: string
  end: string
  seconds: number
}

export type ConsentStatus = 'pending' | 'received' | 'held' | 'abandoned' | 'expired'
export type GrantorKind = 'company' | 'private'
export type ConsentMode = 'new' | 'reconnect' | 'connect_meter'

export interface ConsentResume {
  mode: ConsentMode
  connection_id?: string | null
  external_key?: string | null
}

export interface ConsentGrantor {
  kind: GrantorKind
  name: string | null
  cvr: string | null
}

export interface ConsentRequestCreate {
  expected_grantor: GrantorKind
  return_to: string
  resume: ConsentResume
  scroll_y: number | null
}

export interface ConsentRequestCreated {
  id: string
  consent_url: string
  expires_at: string
}

export interface ConsentRequestOut {
  id: string
  connector: string
  status: ConsentStatus
  expected_grantor: GrantorKind | null
  grantor: ConsentGrantor | null
  point_count: number | null
  connection_id: string | null
  return_to: string | null
  resume: ConsentResume
  scroll_y: number | null
  created_at: string
  expires_at: string
  received_at: string | null
  valid_to: string | null
  hold_reason: string | null
}

export interface PlatformCredentialOut {
  is_set: boolean
  expires_at: string | null
  expires_in_days: number | null
  third_party_id: string | null
  updated_at: string | null
}

export interface AuthorizationOwner {
  request_id: string
  status: ConsentStatus
  customer_id: string
  connection_id: string | null
}

export interface AuthorizationOut {
  id: string
  grantor: ConsentGrantor
  given_at: string
  valid_from: string | null
  valid_to: string | null
  owner: AuthorizationOwner | null
  is_legacy: boolean
}
